import { GoogleGenAI, Type } from "@google/genai";
import { AnalysisResult } from '../types.ts';

// SafeScan Detection Engine Prompt
const SAFESCAN_SYSTEM_INSTRUCTION = `
You are SafeScan, a sophisticated AI content detection engine. Your goal is to provide a forensic TrustScore (0-100%) indicating how likely text is to be human-written.

**Your Analysis Framework (Ensemble Methodology):**
1. **Perplexity (Statistical Probability):** Calculate if word choices are statistically obvious (AI) or surprisingly diverse (Human).
2. **Burstiness (Structural Variance):** Analyze sentence length distribution. AI is uniform; Humans have a jagged, "bursty" rhythm.
3. **Pattern Signature:** Identify common LLM tropes (over-explanation, balanced structures, robotic transitions).
4. **Bidirectional Context:** Scan for predictive word sequences that characteristic of GPT-4, Claude, and Gemini models.

**Output Requirements:**
Return a JSON object:
- \`humanScore\`: Integer 0-100 (The "TrustScore"). 100 = 100% Human confidence.
- \`perplexity\`: "Low", "Medium", or "High".
- \`burstiness\`: "Low", "Medium", or "High".
- \`analysis\`: A professional, data-backed verdict on the text's authenticity.
- \`flaggedSentences\`: Array of up to 3 segments that trigger "AI Signatures".
`;

// Humanize Multi-Pass Rewriting Engine
const HUMANIZE_SYSTEM_INSTRUCTION = (mode: string) => `
You are the Humanize Multi-Pass Rewriting Engine. Your mission is to transform AI text into undetectable human prose while preserving 100% of the original meaning.

**Mode:** ${mode.toUpperCase()}

**Rewriting Protocol (4-Pass Engine):**
1. **Pass 1: Structural Refactoring:** Break up uniform sentence lengths. Mix long, complex compound sentences with punchy 3-word fragments.
2. **Pass 2: Lexical Enrichment:** Swap generic, common AI terminology for visceral, specific human vocabulary. Avoid "delve, tapestry, underscore, paramount".
3. **Pass 3: Conversational Flow:** Inject natural "human messy" elements. Use contractions (don't, it's). Start sentences with "And" or "But" to create a narrative flow.
4. **Pass 4: Pattern Disruption:** Remove the "AI Signature"—monotonous paragraph starts and overly-perfect symmetry.

**Goal:** Achieve a 95%+ TrustScore on SafeScan. Return ONLY the final rewritten text.
`;

const retryOperation = async <T>(operation: () => Promise<T>, retries = 3, delay = 1000): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const analyzeText = async (text: string): Promise<AnalysisResult> => {
  return retryOperation(async () => {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: text,
        config: {
          systemInstruction: SAFESCAN_SYSTEM_INSTRUCTION,
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              humanScore: { type: Type.INTEGER },
              perplexity: { type: Type.STRING, enum: ["Low", "Medium", "High"] },
              burstiness: { type: Type.STRING, enum: ["Low", "Medium", "High"] },
              analysis: { type: Type.STRING },
              flaggedSentences: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    sentence: { type: Type.STRING },
                    issue: { type: Type.STRING },
                    suggestion: { type: Type.STRING }
                  }
                }
              }
            },
            required: ["humanScore", "perplexity", "burstiness", "analysis", "flaggedSentences"],
          }
        }
      });

      if (response.text) {
        return JSON.parse(response.text) as AnalysisResult;
      }
      throw new Error("SafeScan failed to return data.");
    } catch (error: any) {
      console.error("SafeScan Error:", error);
      throw new Error("SafeScan connection timed out.");
    }
  });
};

export const humanizeText = async (text: string, mode: string = 'Standard'): Promise<string> => {
  return retryOperation(async () => {
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-preview', 
        contents: text,
        config: {
          systemInstruction: HUMANIZE_SYSTEM_INSTRUCTION(mode),
          temperature: 0.9,
          topK: 40,
        }
      });
      return response.text || text;
    } catch (error: any) {
      console.error("Humanize Error:", error);
      throw new Error("Humanize engine is currently overloaded.");
    }
  });
};